//client/src/components/ReturnSummaryViewer/ReturnSummaryViewer.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchReturnSummaryXml } from '../../services/returnSummary';
import {
  ParsedReturnSummary,
  ReturnSummaryHeader,
  ReturnSummaryRow,
  normalizeSearch,
  parseReturnSummaryXml,
  returnToSearchText,
} from '../../utils/returnSummaryParser';

function nowIso(): string {
  try {
    return new Date().toISOString();
  } catch {
    return '';
  }
}

export type ReturnSummaryViewerMeta = {
  header: ReturnSummaryHeader;
  returnCount: number;
};

export default function ReturnSummaryViewer({
  returnId,
  onMeta,
}: {
  returnId: string;
  onMeta?: (meta: ReturnSummaryViewerMeta) => void;
}) {
  const [data, setData] = useState<ParsedReturnSummary>({
    header: {
      ClientID: '',
      TaxYear: '',
      ReturnType: '',
      EINorSSN: '',
      OfficeName: '',
      BusinessUnitName: '',
      TaxpayerName: '',
    },
    returns: [],
  });

  const [filteredReturns, setFilteredReturns] = useState<ReturnSummaryRow[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [debugLines, setDebugLines] = useState<string[]>([]);
  const debugOpen = useMemo(() => !!error, [error]);

  const fetchedXmlRef = useRef<string>('');

  const logDebug = useCallback((message: string, obj?: unknown) => {
    const line = `[${nowIso()}] ${message}${obj !== undefined ? ` ${JSON.stringify(obj)}` : ''}`;
    setDebugLines((prev) => (prev.length && prev[0] !== '(No logs yet)' ? [...prev, line] : [line]));
    // eslint-disable-next-line no-console
    console.log(line);
  }, []);

  const applyReturnFilter = useCallback(
    (nextSearch: string, nextIndex?: number) => {
      const q = normalizeSearch(nextSearch);

      let nextFiltered: ReturnSummaryRow[];
      if (!q) {
        nextFiltered = [...data.returns];
      } else {
        nextFiltered = data.returns
          .map((r, originalIndex) => ({ r, originalIndex, hay: returnToSearchText(r) }))
          .filter((x) => x.hay.includes(q))
          .map((x) => ({ ...x.r, _originalIndex: x.originalIndex }));
      }

      setFilteredReturns(nextFiltered);

      if (!nextFiltered.length) {
        setCurrentIndex(0);
        return;
      }

      const clamped = Math.max(0, Math.min(nextIndex ?? currentIndex, nextFiltered.length - 1));
      setCurrentIndex(clamped);
    },
    [currentIndex, data.returns]
  );

  // Fetch + parse when returnId changes.
  useEffect(() => {
    let cancelled = false;

    async function run() {
      setLoading(true);
      setError(null);
      setDebugLines(['(No logs yet)']);
      fetchedXmlRef.current = '';

      logDebug('Loading return summary', { returnId });

      try {
        const resp = await fetchReturnSummaryXml(returnId);
        if (cancelled) return;

        logDebug('API response received', {
          encoding: resp.encoding,
          fetchedAt: resp.fetchedAt,
          bytes: resp.xml?.length,
        });

        fetchedXmlRef.current = resp.xml || '';

        const parsed = parseReturnSummaryXml(resp.xml);
        if (cancelled) return;

        setData(parsed);
        setSearch('');
        setFilteredReturns(parsed.returns);
        setCurrentIndex(0);

        onMeta?.({ header: parsed.header, returnCount: parsed.returns.length });

        logDebug('XML parsed', {
          returns: parsed.returns.length,
          taxpayer: parsed.header.TaxpayerName,
          taxYear: parsed.header.TaxYear,
        });
      } catch (e: any) {
        if (cancelled) return;
        const msg = e?.message || String(e);
        setError(msg);
        setData((prev) => ({ ...prev, returns: [] }));
        setFilteredReturns([]);
        setCurrentIndex(0);
        onMeta?.({
          header: {
            ClientID: '',
            TaxYear: '',
            ReturnType: '',
            EINorSSN: '',
            OfficeName: '',
            BusinessUnitName: '',
            TaxpayerName: '',
          },
          returnCount: 0,
        });
        logDebug('Open job failed', { message: msg, stack: e?.stack || null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [logDebug, onMeta, returnId]);

  // Keyboard navigation (ArrowLeft / ArrowRight).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') {
        setCurrentIndex((idx) => Math.max(0, idx - 1));
      }
      if (e.key === 'ArrowRight') {
        setCurrentIndex((idx) => Math.min(Math.max(0, filteredReturns.length - 1), idx + 1));
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [filteredReturns.length]);

  const current = filteredReturns[currentIndex];

  const title = useMemo(() => {
    if (error) return `Couldn't load XML`;
    if (loading) return 'Loading…';
    if (!filteredReturns.length) return 'No results';
    return current?.jurisdiction || '—';
  }, [current?.jurisdiction, error, filteredReturns.length, loading]);

  const subtitle = useMemo(() => {
    if (error) return 'See debug for details';
    if (loading) return 'Fetching XML and preparing view';
    if (!filteredReturns.length) return '—';

    const details = current?.details || {};
    const form  = details['Form Name'] || '';
    const efile = details['E-file Requested'] || '';
    const due   = details['Due Date'] || '';

    const bits: string[] = [];
    if (form)  bits.push(`Form ${form}`);
    if (efile) bits.push(`E-file: ${efile}`);
    if (due)   bits.push(`Due: ${due}`);
    return bits.length ? bits.join(' • ') : '—';
  }, [current?.details, error, filteredReturns.length, loading]);

  const prevDisabled = currentIndex <= 0 || !filteredReturns.length || loading;
  const nextDisabled = currentIndex >= filteredReturns.length - 1 || !filteredReturns.length || loading;

  const kpiShown = useMemo(() => {
    if (loading) return 'Loading…';
    return `Showing ${filteredReturns.length}`;
  }, [filteredReturns.length, loading]);

  const indexPill = useMemo(() => {
    if (!filteredReturns.length) return 'Return 0 of 0';
    return `Return ${currentIndex + 1} of ${filteredReturns.length}`;
  }, [currentIndex, filteredReturns.length]);

  const onSearchChange = (next: string) => {
    setSearch(next);
    setCurrentIndex(0);
    applyReturnFilter(next, 0);
  };

  const clearSearch = () => {
    onSearchChange('');
  };

  // Keep filter applied when data changes.
  useEffect(() => {
    applyReturnFilter(search, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.returns]);

  return (
    <div className="viewer-wrap">
      {/* Left: jurisdictions list */}
      <section className="card" style={{ boxShadow: 'none' }}>
        <div className="hd">
          <div className="row">
            <div style={{ fontWeight: 700 }}>All jurisdictions</div>
            <span className="kpi">{kpiShown}</span>
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              type="text"
              placeholder="Search (e.g., Georgia, Texas, 1120S)..."
              style={{ flex: 1, minWidth: 210 }}
              aria-label="Search jurisdictions"
            />
            <button className="btn" type="button" onClick={clearSearch} title="Clear search">
              Clear
            </button>
          </div>
        </div>
        <div className="bd">
          <div className="list">
            {loading ? (
              <div className="muted">Loading jurisdictions…</div>
            ) : !filteredReturns.length ? (
              <div className="muted">{error ? `Error: ${error}` : 'No matches. Try a different search.'}</div>
            ) : (
              filteredReturns.map((r, idx) => {
                const details = r.details || {};
                const form  = details['Form Name'] || '';
                const due   = details['Due Date'] || '';
                const efile = details['E-file Requested'] || '';
                const efileNo = efile.toLowerCase() === 'no';

                return (
                  <div
                    key={`${r.jurisdiction}-${idx}`}
                    className={`item${idx === currentIndex ? ' active' : ''}`}
                    onClick={() => setCurrentIndex(idx)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setCurrentIndex(idx);
                    }}
                    style={efileNo ? {
                      backgroundColor: 'rgba(239,68,68,0.13)',
                      border: '1px solid rgba(239,68,68,0.55)',
                      boxShadow: '0 0 14px 3px rgba(239,68,68,0.22)',
                    } : undefined}
                  >
                    <div className="top">
                      <div className="jur">{r.jurisdiction}</div>
                      <div className="kpi">{form ? `Form ${form}` : '—'}</div>
                    </div>
                    <div className="tiny" style={{ marginTop: 6 }}>
                      {due ? `Due: ${due}` : ''}
                      {due && efile ? ' • ' : ''}
                      {efile ? `E-file: ${efile}` : ''}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* Right: details */}
      <section className="card" style={{ boxShadow: 'none' }}>
        <div className="hd">
          <div className="row">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontWeight: 760, fontSize: 18 }}>{title}</div>
              <div className="tiny">{subtitle}</div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
              <button className="btn" type="button" onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))} disabled={prevDisabled}>
                ◀ Prev
              </button>
              <button
                className="btn primary"
                type="button"
                onClick={() => setCurrentIndex((i) => Math.min(filteredReturns.length - 1, i + 1))}
                disabled={nextDisabled}
              >
                Next ▶
              </button>
            </div>
          </div>

          <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="pill">{indexPill}</span>
            <label className="tiny" htmlFor="jump" style={{ marginLeft: 'auto' }}>
              Jump to:
            </label>
            <select
              id="jump"
              value={String(currentIndex)}
              onChange={(e) => setCurrentIndex(parseInt(e.target.value, 10) || 0)}
              disabled={!filteredReturns.length || loading}
            >
              {filteredReturns.map((r, idx) => {
                const form = r.details?.['Form Name'] || '';
                const label = `${idx + 1}. ${r.jurisdiction}${form ? ` (${form})` : ''}`;
                return (
                  <option key={`${r.jurisdiction}-${idx}`} value={String(idx)}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        <div className="bd">
          <table className="viewer-table" aria-label="Return details">
            <tbody>
              {loading ? (
                <tr>
                  <th>Details</th>
                  <td className="muted">Loading…</td>
                </tr>
              ) : error ? (
                <tr>
                  <th>Error</th>
                  <td>{error}</td>
                </tr>
              ) : !filteredReturns.length ? (
                <tr>
                  <th>Details</th>
                  <td className="muted">No detail fields found.</td>
                </tr>
              ) : Object.keys(current?.details || {}).length ? (
                Object.entries(current?.details || {}).map(([k, v]) => (
                  <tr key={k}>
                    <th>{k}</th>
                    <td>{v || '—'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <th>Details</th>
                  <td className="muted">No detail fields found.</td>
                </tr>
              )}
            </tbody>
          </table>

          <details className="debug" style={{ marginTop: 12 }} open={debugOpen}>
            <summary>Debug (expand for verbose network / XML parsing logs)</summary>
            <pre className="debug-log">{debugLines.join('\n')}</pre>
          </details>
        </div>
      </section>
    </div>
  );
}